import { ApiError, apiRequest } from "./http";
import {
  AuthSession,
  AuthUser,
  clearSession,
  getDeviceId,
  isAccessValid,
  isRefreshValid,
  loadSession,
  makeSession,
  saveSession,
} from "./tokenStore";

type SendCodeResponse = {
  success: boolean;
  cooldownSec: number;
};

type LoginResponse = {
  accessToken: string;
  expiresIn: number;
  refreshToken: string;
  refreshExpiresIn: number;
  user: AuthUser;
};

type RefreshResponse = {
  accessToken: string;
  expiresIn: number;
  refreshToken: string;
  refreshExpiresIn: number;
};

export type MeResponse = {
  uid: number;
  phoneMasked: string;
  nickname: string;
  avatar: string;
  language?: string;
};

let refreshTask: Promise<AuthSession> | null = null;

export function getStoredSession(): AuthSession | null {
  const session = loadSession();
  if (!session) {
    return null;
  }

  if (!isRefreshValid(session)) {
    clearSession();
    return null;
  }

  return session;
}

export async function sendLoginCode(phone: string): Promise<SendCodeResponse> {
  return apiRequest<SendCodeResponse>("auth/send-code", {
    method: "POST",
    body: { phone, scene: "login" },
  });
}

export async function loginWithCode(phone: string, code: string): Promise<AuthSession> {
  const deviceId = getDeviceId();
  const response = await apiRequest<LoginResponse>("auth/login", {
    method: "POST",
    body: {
      phone,
      code,
      device: {
        id: deviceId,
        platform: "teacher-web",
      },
    },
  });
  const session = makeSession({ ...response, deviceId });
  saveSession(session);
  return session;
}

async function refreshSession(session: AuthSession): Promise<AuthSession> {
  const response = await apiRequest<RefreshResponse>("auth/refresh", {
    method: "POST",
    body: { refreshToken: session.refreshToken },
  });
  const nextSession = makeSession({
    ...response,
    user: session.user,
    deviceId: session.deviceId,
  });
  saveSession(nextSession);
  return nextSession;
}

export async function ensureAccessToken(): Promise<string> {
  const session = getStoredSession();
  if (!session) {
    throw new ApiError(401, "请先登录");
  }

  if (isAccessValid(session)) {
    return session.accessToken;
  }

  refreshTask ??= refreshSession(session).finally(() => {
    refreshTask = null;
  });

  const refreshed = await refreshTask;
  return refreshed.accessToken;
}

export async function authenticatedRequest<T>(
  path: string,
  options: Omit<Parameters<typeof apiRequest<T>>[1], "accessToken"> = {},
): Promise<T> {
  const accessToken = await ensureAccessToken();

  try {
    return await apiRequest<T>(path, { ...options, accessToken });
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      clearSession();
    }
    throw error;
  }
}

export async function fetchMe(): Promise<MeResponse> {
  return authenticatedRequest<MeResponse>("me");
}

export function logoutLocally(): void {
  clearSession();
}
